/**
 * Session Results Report Page
 *
 * Build Contract §2.1 compliance:
 *   - Primary data source: `session_results` table (DB canonical read path).
 *   - Secondary / enrichment: `session.rankingOutput` JSONB on the session row
 *     (provides classification, normalized scores, tier data not in session_results).
 *
 * Build Contract §2.4 compliance:
 *   - Component calls fetchSessionById on mount so a direct link or page refresh
 *     reconstructs the session from DB rather than showing "Not Found".
 *
 * Build Contract §3 (Definition of Done):
 *   - Artifact created: session_results rows written by persistSessionResults.
 *   - Artifact viewed: this component.
 *   - Artifact survives refresh: fetchSessionById on mount.
 *   - Artifact reloads in new session: same fetchSessionById path.
 */

import { ObjectKindLabel } from '@/components/ds/ObjectRef';
import { OBJECT_KINDS } from '@/components/ds/objectKinds';
import { useEffect, useMemo, useState } from 'react';
import { useParams, useLocation, useSearchParams, Link } from 'react-router-dom';
import { SessionValuesWaitingNote } from '../../../components/prioritization/sessions/SessionValuesWaitingNote';
import {
  ChevronLeft,
  FileText,
  Clock,
  Users,
  Target,
  ListOrdered,
  CheckCircle,
  AlertTriangle,
  Info,
  Calendar,
  User,
  Scale,
  ArrowUpDown,
  ExternalLink,
  Loader2,
} from 'lucide-react';
import { useSessionStore } from '../../../stores/prioritization/sessionStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useDegapProcessStore } from '../../../stores/prioritization/degapProcessStore';
import { useTrackStore } from '../../../stores/prioritization/trackStore';
import { getSafeSupabaseClient } from '../../../lib/supabaseSync';
import { SessionMemoAction } from '../../../components/prioritization/memo/SessionMemoAction';
import { SESSION_TYPE_LABELS } from '../../../types/prioritization/session';
import type { PrioritizationSession } from '../../../types/prioritization/session';
import { RankOverrideList, TiePanel } from '../../../components/prioritization/RankOverridePanel';
import { RevealOrchestrator } from '../../../features/sessions-participant/components/results/RevealOrchestrator';
import { useSessionReveal } from '@/hooks/useSessionReveal';
import { readTies, sharedRanks } from '@/lib/sessions/rankTies';
import { sessionOwnItemTitles } from '@/lib/sessions/sessionItemTitles';
import { namesHidden, useSessionRoomRules } from '@/lib/sessions/roomRules';
import { useSessionRankChangeRule } from '@/lib/sessions/rankChangeRule';
import { formatRank } from '@/lib/itemRankings';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { buildCatchballInboxUrl } from '../../../lib/catchballInboxUrls';
import {
  catchballQueryFromSearch,
  resolveLoopIdForSession,
} from '../../../lib/catchballSessionLink';
import { useCatchballLoopStore } from '../../../stores/prioritization/catchballLoopStore';
import {
  CHIP_OVERFLOW_CLASS,
  CLASSIFICATION_CHIP_CLASS,
  getVisibleClassificationEntries,
} from '../../../lib/classificationDisplay';
import { MatrixToolResultsCharts } from '../../../components/prioritization/reports/MatrixToolResultsCharts';
import {
  resolveMatrixReportVariant,
  quadrantMetaByKey,
  type MatrixReportVariant,
} from '../../../lib/sessionResultVisualizations';
import { useActorDisplayMap } from '../../../hooks/prioritization/useActorDisplayMap';
import { AttachSessionToBallCard } from '../../../components/prioritization/catchball/AttachSessionToBallCard';
import { SendThisUpCard } from '../../../components/prioritization/catchball/SendThisUpCard';
import { ItemRelationshipFlags } from '@/components/prioritization/ItemRelationshipFlags';
import { useItemLinkScope } from '@/lib/itemLinkScope';

// ============================================================================
// Types
// ============================================================================

/** Row shape returned from session_results table */
interface SessionResultRow {
  id: string;
  session_id: string;
  track_id: string | null;
  item_id: string;
  rank: number;
  score: number | null;
  result_status: string | null;
  classification: string | null;
  tool_id: string | null;
  created_at: string;
  workspace_id: string | null;
}

// Eisenhower quadrant labels — used when tool_id indicates Eisenhower
const EISENHOWER_LABELS: Record<string, { label: string; action: string; color: string }> = {
  ui: { label: 'Do',        action: 'Urgent + Important',       color: 'text-[var(--coral)] bg-[var(--coral-dim)] border-[var(--coral)]' },
  in: { label: 'Schedule',  action: 'Important, Not Urgent',    color: 'text-[var(--mustard)] bg-[var(--mustard-dim)] border-[var(--mustard)]' },
  un: { label: 'Delegate',  action: 'Urgent, Not Important',    color: 'text-[var(--text-sec)] bg-[var(--slate-dim)] border-[var(--slate)]' },
  nn: { label: 'Eliminate', action: 'Not Urgent, Not Important', color: 'text-[var(--text-ter)] bg-[var(--surface)] border-[var(--rule)]' },
};

// MoSCoW tier labels
const MOSCOW_LABELS: Record<string, { label: string; color: string }> = {
  must:   { label: 'Must Have',    color: 'text-[var(--coral)] bg-[var(--coral-dim)] border-[var(--coral)]' },
  should: { label: 'Should Have',  color: 'text-[var(--mustard)] bg-[var(--mustard-dim)] border-[var(--mustard)]' },
  could:  { label: 'Could Have',   color: 'text-[var(--text-sec)] bg-[var(--slate-dim)] border-[var(--slate)]' },
  wont:   { label: "Won't Have",   color: 'text-[var(--text-ter)] bg-[var(--surface)] border-[var(--rule)]' },
};

function getClassificationChip(toolId: string | null, classification: string | null) {
  if (!classification) return null;
  const t = (toolId ?? '').toLowerCase();
  if (t.includes('eisenhower')) {
    const meta = EISENHOWER_LABELS[classification];
    if (meta) return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium border rounded-full ${meta.color}`}>
        {meta.label} <span className="opacity-60">— {meta.action}</span>
      </span>
    );
  }
  if (t.includes('moscow')) {
    const meta = MOSCOW_LABELS[classification];
    if (meta) return (
      <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium border rounded-full ${meta.color}`}>
        {meta.label}
      </span>
    );
  }
  if (t.includes('impact') && t.includes('effort')) {
    const meta = quadrantMetaByKey('impact_effort', classification);
    if (meta) return (
      <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium border rounded-full ${meta.bgClass} ${meta.textClass} ${meta.borderClass}`}>
        {meta.label}
        {meta.subtitle && <span className="opacity-60 ml-1">— {meta.subtitle}</span>}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium border rounded-full bg-[var(--surface)] text-[var(--text-sec)] border-[var(--rule)]">
      {classification}
    </span>
  );
}

// ============================================================================
// Component
// ============================================================================

export function SessionResultsReport() {
  const { sessionId } = useParams<{ sessionId: string }>();
  // Lock moment (Doc 26 rule 6): set by LiveSessionPage when results were just
  // locked, so everyone lands on a confirmation, not a bare report.
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const lockMoment = (location.state as { lockMoment?: boolean } | null)?.lockMoment === true;
  // Set when close could not put this sitting on a ball. Previously the miss was
  // swallowed and the session simply never appeared in the Inbox.
  const attachFailure = (location.state as { sessionAttachFailure?: string } | null)
    ?.sessionAttachFailure;
  const inboxCtx = catchballQueryFromSearch(searchParams);

  // Store access
  const sessions     = useSessionStore(s => s.sessions);
  const fetchSession = useSessionStore(s => s.fetchSessionById);
  const cycles       = useDegapProcessStore(s => s.cycles);
  const items        = useItemsStore(s => s.items);
  const tracks       = useTrackStore(s => s.tracks);

  const session = useMemo(
    () => sessions.find(s => s.id === sessionId),
    [sessions, sessionId]
  );
  useItemLinkScope({ cycleId: session?.cycleId });

  const catchballLoops = useCatchballLoopStore((s) => s.loops);
  const fetchCatchballLoops = useCatchballLoopStore((s) => s.fetchFromSupabase);
  const resolvedLoopId = useMemo(
    () =>
      resolveLoopIdForSession({
        urlLoopId: inboxCtx.loopId,
        sessionId,
        trackId: session?.trackId,
        cycleId: session?.cycleId,
        loops: catchballLoops,
      }),
    [inboxCtx.loopId, sessionId, session?.trackId, session?.cycleId, catchballLoops],
  );
  const deskLoop = useMemo(
    () => (resolvedLoopId ? catchballLoops.find((l) => l.id === resolvedLoopId) ?? null : null),
    [catchballLoops, resolvedLoopId],
  );

  const catchballInboxHref =
    resolvedLoopId && (deskLoop?.cycleId || session?.cycleId)
      ? buildCatchballInboxUrl(
          (deskLoop?.cycleId || session?.cycleId) as string,
          resolvedLoopId,
          { ballId: inboxCtx.ballId },
        )
      : null;

  // §2.4: Reconstruct from DB on mount so direct links and page refreshes work.
  const [sessionLoading, setSessionLoading] = useState(!session);
  useEffect(() => {
    if (!sessionId) return;
    if (session) { setSessionLoading(false); return; }
    setSessionLoading(true);
    void fetchSession(sessionId).finally(() => setSessionLoading(false));
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ensure loops are loaded so track→desk resolution works on a cold results URL.
  useEffect(() => {
    const cycleId = session?.cycleId;
    if (!cycleId) return;
    void fetchCatchballLoops(cycleId);
  }, [session?.cycleId, fetchCatchballLoops]);

  // §2.1: Load session_results rows from DB — the canonical durable read path.
  // These rows were written by persistSessionResults before RUN_COMPLETE transition.
  const [dbRows, setDbRows] = useState<SessionResultRow[]>([]);
  const [dbRowsLoading, setDbRowsLoading] = useState(true);
  const [dbRowsReload, setDbRowsReload] = useState(0);
  useEffect(() => {
    if (!sessionId) return;
    const supabase = getSafeSupabaseClient();
    if (!supabase) { setDbRowsLoading(false); return; }
    setDbRowsLoading(true);
    supabase
      .from('session_results')
      .select('id, session_id, track_id, item_id, rank, score, result_status, classification, tool_id, created_at, workspace_id')
      .eq('session_id', sessionId)
      .order('rank', { ascending: true })
      .then(({ data }) => {
        setDbRows((data as SessionResultRow[]) ?? []);
        setDbRowsLoading(false);
      });
  }, [sessionId, dbRowsReload]);

  const cycle = useMemo(
    () => session?.cycleId ? cycles.find(c => c.id === session.cycleId) : undefined,
    [cycles, session?.cycleId]
  );

  const track = useMemo(
    () => session?.trackId ? tracks.find(t => t.id === session.trackId) : undefined,
    [tracks, session?.trackId]
  );

  // Names the session holds itself: items typed into the Event Designer
  // (generated ids like item_9rbxi1e), criteria ranked as items, isolated lines.
  const ownTitles = useMemo(
    () => sessionOwnItemTitles({ modeConfig: session?.modeConfig, isolatedItems: session?.isolatedItems }),
    [session?.modeConfig, session?.isolatedItems],
  );

  const getItemTitle = (itemId: string) => {
    const workspaceItem = items.find(i => i.id === itemId);
    if (workspaceItem) return workspaceItem.title;
    if (ownTitles[itemId]) return ownTitles[itemId];

    // Isolated sessions use the text itself as the itemId (no workspace lookup needed)
    if (session?.sessionContextMode !== 'cycle_linked') return itemId;
    return 'Unknown Item';
  };

  const participantAndFacilitatorIds = useMemo(
    () => [session?.facilitatorId, ...(session?.participantIds ?? [])],
    [session?.facilitatorId, session?.participantIds]
  );
  const actorDisplayMap = useActorDisplayMap(participantAndFacilitatorIds);

  // The host console's after-close pieces (S-4, S-5, S-3): the paced reveal,
  // ties and who may break them, and whether names are shown.
  const userId = useUserStore(s => s.userId);
  const { rules: roomRules } = useSessionRoomRules(sessionId);
  const { rule: rankRule, reload: reloadRankRule } = useSessionRankChangeRule(sessionId, session?.state);
  const isFacilitator = !!userId && session?.facilitatorId === userId;
  const revealCount = dbRows.length || session?.rankingOutput?.rankedItemIds?.length || 0;
  const reveal = useSessionReveal(sessionId, revealCount);

  // Loading state: show spinner while session is being fetched from DB
  if (sessionLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 text-[var(--text-ter)] animate-spin" />
        <span className="ml-3 text-sm text-[var(--text-ter)]">Loading session results…</span>
      </div>
    );
  }

  // Session not found even after DB fetch
  if (!session) {
    return (
      <div className="px-4 py-12">
        <div className="bg-[var(--coral-dim)] border border-[var(--coral)] p-6 text-center">
          <AlertTriangle className="h-12 w-12 text-[var(--coral)] mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-[var(--coral)] mb-2">Session Not Found</h1>
          <p className="text-[var(--coral)] mb-4">
            The session with ID "{sessionId}" does not exist or you do not have access to it.
          </p>
          <Link
            to="/prioritization/reports"
            className="inline-flex items-center gap-2 text-[var(--coral)] hover:text-[var(--coral)]"
          >
            <ChevronLeft className="h-4 w-4" />
            Back to Reports
          </Link>
        </div>
      </div>
    );
  }

  const { rankingOutput } = session;
  const ties = readTies((rankingOutput as { ties?: unknown } | undefined)?.ties);
  // Ruling R10: a live session's result is revealed by the host, at the host's
  // pace; until then a participant's page shows the reveal, not the list. An
  // offline session is one with an answer window (R14); Session-app sessions
  // are marked async even when they run in the room, so that mark alone does
  // not make a session offline.
  const isLiveRun = !(session.modeConfig?.timingMode === 'async' && session.modeConfig?.closesAt);
  const revealHere = isLiveRun && session.state === 'RUN_COMPLETE';
  const holdForReveal = revealHere && !isFacilitator && !roomRules.isAdmin && reveal.phase !== 'complete';
  const hideRecordNames = namesHidden(roomRules, 'record');

  // For V3 criteria sessions ranking_output is written to DB but rankedItemIds are
  // crit_xxxxx IDs. If DB rows are empty (criteria don't go to session_results),
  // also check v3Config.criteriaRanking as a display-only fallback.
  // (Declared before hasOutput — referencing it below would hit the const TDZ
  // and crash the no-results render path.)
  const v3Config = (session.modeConfig as Record<string, unknown>)
    ?.v3Config as {
      criteria?: Array<{ id: string; name: string; weight?: number }>;
      criteriaRanking?: string[];
    } | undefined;

  const hasOutput = !!rankingOutput || dbRows.length > 0 || !!(v3Config?.criteriaRanking?.length);
  const overrides = rankingOutput?.overrides ?? [];
  const isCriteriaResultSession =
    !!(v3Config?.criteriaRanking?.length) &&
    !rankingOutput?.rankedItemIds?.length &&
    dbRows.length === 0;

  // Determine the effective ranked item list:
  // - Primary: session_results DB rows (§2.1 canonical) — already sorted by rank ASC.
  // - Fallback: rankingOutput.rankedItemIds if DB rows not yet available.
  const effectiveRankedRows: Array<{
    itemId: string;
    rank: number;
    score: number | null;
    classification: string | null;
    toolId: string | null;
  }> = dbRows.length > 0
    ? [...dbRows]
        // Tied rows share a rank; keep them in the saved order.
        .sort((a, b) => a.rank - b.rank
          || (rankingOutput?.rankedItemIds ?? []).indexOf(a.item_id) - (rankingOutput?.rankedItemIds ?? []).indexOf(b.item_id))
        .map(r => ({
        itemId: r.item_id,
        rank: r.rank,
        score: r.score,
        classification: r.classification,
        toolId: r.tool_id,
      }))
    : (rankingOutput?.rankedItemIds ?? []).map((itemId, idx) => ({
        itemId,
        rank: sharedRanks(rankingOutput?.rankedItemIds ?? [], ties)[itemId]?.rank ?? idx + 1,
        score: rankingOutput?.scores?.[itemId]?.total ?? null,
        classification: (() => {
          const cl = rankingOutput?.classifications?.[itemId];
          if (!cl) return null;
          if (typeof cl === 'string') return cl;
          return (cl as Record<string, unknown>).primaryCategory as string ?? null;
        })(),
        toolId: rankingOutput?.toolId ?? null,
      }))
    // Fallback: v3Config.criteriaRanking (criteria sessions before ranking_output is synced).
    .concat(isCriteriaResultSession
      ? (v3Config!.criteriaRanking ?? []).map((itemId, idx) => {
          const crit = v3Config!.criteria?.find(c => c.id === itemId);
          return {
            itemId,
            rank: idx + 1,
            score: crit?.weight ?? null,
            classification: null,
            toolId: 'pairwise_ranking',
          };
        })
      : []);

  // The database holds no cut line as null (the type says undefined). With no
  // line drawn the ranking does not say what made the cut, so the table makes
  // no claim either way: no Status column, no dimmed rows. Before, null read as
  // "cut at 0" and every row said "Below cut line".
  const cutLine = typeof session.cutLinePosition === 'number' ? session.cutLinePosition : null;

  const toolIdForDisplay = dbRows[0]?.tool_id ?? rankingOutput?.toolId ?? session.selectedToolId;
  const matrixVariant = resolveMatrixReportVariant(toolIdForDisplay);

  return (
    <div className="px-6 py-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-2">
          {catchballInboxHref ? (
            <Link
              to={catchballInboxHref}
              className="inline-flex items-center text-sm font-semibold text-[var(--text-ter)] hover:text-[var(--text-ter)]"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back to Catchball
            </Link>
          ) : null}
          <Link
            to="/prioritization/reports"
            className="inline-flex items-center text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)]"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to Reports
          </Link>
        </div>
        <div className="flex items-start justify-between">
          <div>
            <ObjectKindLabel kind="session_result" />
            <h1 className="text-2xl font-bold text-[var(--text-pri)]">{session.name}</h1>
            <p className="text-[var(--text-ter)] mt-1 text-sm">Session Results — Immutable Record</p>
          </div>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            session.state === 'PUBLISHED'
              ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]'
              : session.state === 'RUN_COMPLETE'
              ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]'
              : 'bg-[var(--hover-tint)] text-[var(--text-pri)]'
          }`}>
            {session.state}
          </span>
        </div>
      </div>

      {/* The results are saved either way, but a sitting that never landed on a
          ball cannot be sent up — say so here rather than letting the desk
          quietly show nothing. */}
      {lockMoment && attachFailure && (
        <div className="mb-4 border border-[var(--mustard)] bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/30 p-4">
          <p className="text-sm font-semibold text-[var(--mustard)] dark:text-[var(--mustard)]">
            Saved, but not attached to a ball
          </p>
          <p className="mt-1 text-sm text-[var(--mustard)] dark:text-[var(--mustard)]">
            {attachFailure === 'no_loop'
              ? 'This session is not on an open Catchball loop, so it will show as “Not attached” on the Catchball. Open the Inbox for its track and use Attach to ball before passing results up.'
              : 'The session could not be attached to its ball. Open the Catchball for its track and use Attach to ball — you may need to take the ball first.'}
          </p>
        </div>
      )}

      {/* A cycle-linked session's values are for this session; say how many still
          wait to be accepted as official (roadmap 1C, ruling 4). */}
      {(session.cycleId || session.trackId) && (
        <div className="mb-4">
          <SessionValuesWaitingNote sessionId={session.id} hideWhenNoneWaiting load />
        </div>
      )}

      {/* Lock moment — the just-locked confirmation everyone lands on (Doc 26 rule 6) */}
      {lockMoment && hasOutput && (
        <div className="mb-6 border border-[var(--green)] bg-[var(--green-dim)] p-5">
          <div className="flex items-start gap-3">
            <CheckCircle className="h-7 w-7 text-[var(--green)] flex-shrink-0" />
            <div className="flex-1">
              <p className="text-lg font-semibold text-[var(--green)] dark:text-[var(--text-ter)]">
                Done — {effectiveRankedRows.length} item{effectiveRankedRows.length !== 1 ? 's' : ''} ranked
                {session.participantIds.length > 0 && (
                  <> by {session.participantIds.length} {session.participantIds.length !== 1 ? 'people' : 'person'}</>
                )}
              </p>
              <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)] mt-1">
                This result is saved as an immutable record. It survives refresh and is
                visible to everyone who took part.
                {catchballInboxHref
                  ? ' Return to Catchball to package it into the next send.'
                  : null}
              </p>
              {catchballInboxHref && (
                <Link
                  to={catchballInboxHref}
                  className="mt-3 inline-flex items-center bg-[var(--slate)] px-3 py-2 text-sm font-semibold text-white hover:bg-[var(--slate)]"
                >
                  Return to Catchball
                  <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
                </Link>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Attach / return to Catchball — mid-tier pass-up packaging lives in the Inbox */}
      <AttachSessionToBallCard
        session={session}
        urlLoopId={inboxCtx.loopId}
        urlBallId={inboxCtx.ballId}
      />

      {/* Send this up — the C Mode exchange step (renders only when applicable) */}
      <SendThisUpCard session={session} rankedItemCount={effectiveRankedRows.length} />

      {/* Incomplete warning */}
      {!hasOutput && (
        <div className="bg-[var(--mustard-dim)] border border-[var(--mustard)] p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-[var(--mustard)] mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-medium text-[var(--mustard)]">No results yet</h3>
              <p className="text-sm text-[var(--mustard)] mt-1">
                This session has not been run or results have not been saved.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── Section 1: Session Context ─────────────────────────────────────── */}
      <section className="bg-[var(--bg-raised)] border border-[var(--rule)] mb-6">
        <div className="px-6 py-4 border-b border-[var(--rule)] bg-[var(--surface)]">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-[var(--text-sec)]" />
            <h2 className="text-lg font-semibold text-[var(--text-pri)]">Session Context</h2>
          </div>
        </div>
        <div className="p-6">
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <dt className="text-sm font-medium text-[var(--text-ter)]">Session Type</dt>
              <dd className="text-sm text-[var(--text-pri)] mt-1">
                {SESSION_TYPE_LABELS[session.sessionType] ?? session.sessionType}
              </dd>
            </div>

            {/* Cycle — if session belongs to one */}
            <div>
              <dt className="text-sm font-medium text-[var(--text-ter)] flex items-center gap-1">
                <OBJECT_KINDS.cycle.icon className="h-3.5 w-3.5" />
                Cycle
              </dt>
              <dd className="text-sm mt-1">
                {cycle ? (
                  <Link
                    to={`/prioritization/cycles/${cycle.id}`}
                    className="text-primary-600 hover:text-primary-700 font-medium"
                  >
                    {cycle.name}
                  </Link>
                ) : (
                  <span className="text-[var(--text-ter)]">Standalone session</span>
                )}
              </dd>
            </div>

            {/* Track — the scope lane */}
            <div>
              <dt className="text-sm font-medium text-[var(--text-ter)] flex items-center gap-1">
                <OBJECT_KINDS.track.icon className="h-3.5 w-3.5" />
                Track
              </dt>
              <dd className="text-sm mt-1">
                {track ? (
                  <Link
                    to={`/prioritization/tracks/${track.id}`}
                    className="text-primary-600 hover:text-primary-700 font-medium"
                  >
                    {track.name}
                  </Link>
                ) : session.trackId ? (
                  <span className="text-[var(--text-ter)] font-mono text-xs">{session.trackId}</span>
                ) : (
                  <span className="text-[var(--text-ter)]">No track</span>
                )}
              </dd>
            </div>

            <div>
              <dt className="text-sm font-medium text-[var(--text-ter)]">Tool Used</dt>
              <dd className="text-sm text-[var(--text-pri)] mt-1 flex items-center gap-2">
                <Scale className="h-4 w-4 text-[var(--text-ter)]" />
                {rankingOutput?.toolName ?? toolIdForDisplay ?? '—'}
              </dd>
            </div>

            <div>
              <dt className="text-sm font-medium text-[var(--text-ter)]">Created</dt>
              <dd className="text-sm text-[var(--text-pri)] mt-1 flex items-center gap-2">
                <Calendar className="h-4 w-4 text-[var(--text-ter)]" />
                {session.createdAt.toLocaleDateString()} at {session.createdAt.toLocaleTimeString()}
              </dd>
            </div>

            <div>
              <dt className="text-sm font-medium text-[var(--text-ter)]">Run Completed</dt>
              <dd className="text-sm text-[var(--text-pri)] mt-1 flex items-center gap-2">
                <Clock className="h-4 w-4 text-[var(--text-ter)]" />
                {session.runCompletedAt
                  ? `${session.runCompletedAt.toLocaleDateString()} at ${session.runCompletedAt.toLocaleTimeString()}`
                  : <span className="text-[var(--text-ter)]">Not yet run</span>
                }
              </dd>
            </div>

            <div>
              <dt className="text-sm font-medium text-[var(--text-ter)]">Facilitator</dt>
              <dd className="text-sm text-[var(--text-pri)] mt-1 flex items-center gap-2">
                <User className="h-4 w-4 text-[var(--text-ter)]" />
                {actorDisplayMap[session.facilitatorId] ?? session.facilitatorId}
              </dd>
            </div>

            <div>
              <dt className="text-sm font-medium text-[var(--text-ter)]">Participants</dt>
              <dd className="text-sm text-[var(--text-pri)] mt-1 flex items-center gap-2">
                <Users className="h-4 w-4 text-[var(--text-ter)]" />
                {session.participantIds.length} participant{session.participantIds.length !== 1 ? 's' : ''}
              </dd>
            </div>

            {session.resultingItemPoolId && (
              <div>
                <dt className="text-sm font-medium text-[var(--text-ter)]">Result Pool</dt>
                <dd className="text-sm mt-1">
                  <Link
                    to={`/prioritization/lists/${session.resultingItemPoolId}`}
                    className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700"
                  >
                    View Result Pool <ExternalLink className="h-4 w-4" />
                  </Link>
                </dd>
              </div>
            )}
          </dl>
        </div>
      </section>

      {/* ── Section 2: Inputs ─────────────────────────────────────────────── */}
      <section className="bg-[var(--bg-raised)] border border-[var(--rule)] mb-6">
        <div className="px-6 py-4 border-b border-[var(--rule)] bg-[var(--surface)]">
          <div className="flex items-center gap-2">
            <Target className="h-5 w-5 text-[var(--text-sec)]" />
            <h2 className="text-lg font-semibold text-[var(--text-pri)]">
              Inputs — {session.sessionContextMode !== 'cycle_linked' ? (session.isolatedItems?.filter(t => t.trim()).length ?? 0) : session.itemIds.length} items
            </h2>
          </div>
        </div>
        <div className="p-6">
          <h3 className="text-sm font-medium text-[var(--text-pri)] mb-3">Items Included</h3>
          {session.sessionContextMode !== 'cycle_linked' ? (
            (session.isolatedItems?.filter(t => t.trim()) ?? []).length > 0 ? (
              <ul className="space-y-2">
                {(session.isolatedItems?.filter(t => t.trim()) ?? []).map((text, index) => (
                  <li key={text} className="flex items-center gap-3 text-sm">
                    <span className="w-6 h-6 rounded-full bg-[var(--hover-tint)] flex items-center justify-center text-xs text-[var(--text-sec)] flex-shrink-0">
                      {index + 1}
                    </span>
                    <span className="text-[var(--text-pri)]">{text}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-[var(--text-ter)]">No items were included in this session.</p>
            )
          ) : session.itemIds.length > 0 ? (
            <ul className="space-y-2">
              {session.itemIds.map((itemId, index) => (
                <li key={itemId} className="flex items-center gap-3 text-sm">
                  <span className="w-6 h-6 rounded-full bg-[var(--hover-tint)] flex items-center justify-center text-xs text-[var(--text-sec)] flex-shrink-0">
                    {index + 1}
                  </span>
                  <span className="text-[var(--text-pri)]">{getItemTitle(itemId)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-[var(--text-ter)]">No items were included in this session.</p>
          )}

          {session.criteriaConfig?.criteriaIds?.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-medium text-[var(--text-pri)] mb-3">Criteria & Weights</h3>
              <ul className="space-y-2">
                {session.criteriaConfig.criteriaIds.map(criteriaId => {
                  const weight = session.criteriaConfig.weights?.[criteriaId];
                  return (
                    <li key={criteriaId} className="flex items-center justify-between text-sm">
                      <span className="text-[var(--text-pri)]">{criteriaId}</span>
                      {weight !== undefined && (
                        <span className="px-2 py-0.5 bg-[var(--slate-dim)] text-[var(--text-sec)] rounded text-xs">
                          {(weight * 100).toFixed(0)}%
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </section>

      {/* S-4: the paced reveal — the host's controls, or what a participant follows */}
      {revealHere && hasOutput && (isFacilitator || holdForReveal) && (
        <div className="mb-6" data-testid="report-reveal">
          <RevealOrchestrator
            sessionId={session.id}
            isFacilitator={isFacilitator}
            items={effectiveRankedRows.map(r => ({ rank: r.rank, title: getItemTitle(r.itemId), score: r.score ?? 0 }))}
          />
        </div>
      )}

      {/* S-5: ties, with the rule; the facilitator may break one with a reason */}
      {!holdForReveal && ties.length > 0 && (
        <div className="mb-6">
          <TiePanel
            sessionId={session.id}
            rankedItemIds={rankingOutput?.rankedItemIds ?? []}
            ties={ties}
            getItemTitle={getItemTitle}
            canBreak={session.state === 'RUN_COMPLETE' && !!rankRule?.canBreakTies}
            onDone={async () => {
              await fetchSession(session.id);
              reloadRankRule();
              setDbRowsReload(n => n + 1);
            }}
          />
        </div>
      )}

      {/* ── Section 3: Results — canonical read from session_results rows ────── */}
      {!holdForReveal && (
      <section className="bg-[var(--bg-raised)] border border-[var(--rule)] mb-6">
        <div className="px-6 py-4 border-b border-[var(--rule)] bg-[var(--surface)]">
          <div className="flex items-center gap-2">
            <ListOrdered className="h-5 w-5 text-[var(--text-sec)]" />
            <h2 className="text-lg font-semibold text-[var(--text-pri)]">
              {isCriteriaResultSession ? 'Criteria Weights — Final Ranking' : 'Results — Rankings & Scores'}
            </h2>
            {dbRows.length > 0 && (
              <span className="ml-auto text-xs text-[var(--text-ter)] font-normal flex items-center gap-1">
                <CheckCircle className="h-3.5 w-3.5 text-[var(--green)]" />
                {dbRows.length} items saved to database
              </span>
            )}
          </div>
        </div>

        {dbRowsLoading ? (
          <div className="p-8 flex items-center justify-center gap-2 text-[var(--text-ter)]">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm">Loading results from database…</span>
          </div>
        ) : !hasOutput ? (
          <div className="p-6 text-center">
            <Info className="h-8 w-8 text-[var(--text-ter)] mx-auto mb-2" />
            <p className="text-sm text-[var(--text-ter)] mb-4">
              No results yet. This session has not been run.
            </p>
          </div>
        ) : isCriteriaResultSession || (effectiveRankedRows.length > 0 && effectiveRankedRows[0]?.itemId?.startsWith('crit_')) ? (
          // Criteria-weight view: show each criterion ranked by weight with a bar
          <div className="p-6 space-y-3">
            <p className="text-xs text-[var(--text-ter)] mb-4">
              Weights were derived from pairwise comparisons by all participants.
              A higher weight means this criterion should carry more influence when ranking items.
            </p>
            {effectiveRankedRows.map(({ itemId, rank, score }) => {
              const weight = typeof score === 'number' ? score : null;
              return (
                <div key={itemId} className="flex items-center gap-4">
                  <span className={`w-7 shrink-0 text-right text-sm font-bold ${rank <= 3 ? 'text-[var(--mustard)]' : 'text-[var(--text-ter)]'}`}>
                    {rank}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-[var(--text-pri)]">{getItemTitle(itemId)}</span>
                      {weight !== null && (
                        <span className="text-xs font-semibold text-[var(--text-ter)] ml-2 shrink-0">{weight}%</span>
                      )}
                    </div>
                    {weight !== null && (
                      <div className="h-2 rounded-full bg-[var(--hover-tint)] overflow-hidden">
                        <div
                          className="h-2 rounded-full bg-[var(--slate)]"
                          style={{ width: `${Math.max(weight, 2)}%` }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : matrixVariant ? (
          <MatrixSessionResultsView
            variant={matrixVariant}
            rows={effectiveRankedRows}
            rankingOutput={rankingOutput}
            getItemTitle={getItemTitle}
          />
        ) : (
          // Generic ranked table
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[var(--rule)]">
              <thead className="bg-[var(--surface)]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide">Rank</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide">Item</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide">Classification</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide">Score</th>
                  {rankingOutput?.normalizedScores && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide">Normalized</th>
                  )}
                  {cutLine !== null && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide">Status</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--rule)]">
                {effectiveRankedRows.map(({ itemId, rank, score, classification, toolId: rowToolId }, index) => {
                  const isAboveCutLine = cutLine === null || index < cutLine;
                  const isCutLine = cutLine !== null && index === cutLine;

                  // Enrich with rankingOutput classification data when present
                  const rankingClassifications = rankingOutput
                    ? getVisibleClassificationEntries(rankingOutput.classifications?.[itemId])
                    : [];

                  return (
                    <tr
                      key={itemId}
                      className={`${isCutLine ? 'border-t-4 border-[var(--coral)]' : ''} ${!isAboveCutLine ? 'bg-[var(--surface)] opacity-60' : ''}`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
                          rank <= 3 ? 'bg-[var(--mustard-dim)] text-[var(--mustard)]' : 'bg-[var(--hover-tint)] text-[var(--text-sec)]'
                        }`}>
                          {formatRank(rank, effectiveRankedRows.filter(r => r.rank === rank).length > 1)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-[var(--text-pri)]">{getItemTitle(itemId)}</div>
                        <ItemRelationshipFlags itemId={itemId} className="mt-1" />
                      </td>
                      <td className="px-6 py-4">
                        {classification ? (
                          getClassificationChip(rowToolId, classification)
                        ) : rankingClassifications.length > 0 ? (
                          <div className="flex flex-wrap gap-1.5">
                            {rankingClassifications.slice(0, 3).map(entry => (
                              <span key={`${itemId}-${entry.key}`} className={CLASSIFICATION_CHIP_CLASS}>
                                {entry.label}: {entry.value}
                              </span>
                            ))}
                            {rankingClassifications.length > 3 && (
                              <span className={CHIP_OVERFLOW_CLASS} title={rankingClassifications.map(e => `${e.label}: ${e.value}`).join(', ')}>
                                +{rankingClassifications.length - 3} more
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-[var(--text-ter)]">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--text-pri)]">
                        {score != null ? score.toFixed(2) : '—'}
                      </td>
                      {rankingOutput?.normalizedScores && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--text-ter)]">
                          {(rankingOutput.normalizedScores[itemId] ?? 0).toFixed(0)}%
                        </td>
                      )}
                      {cutLine !== null && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          {isAboveCutLine ? (
                            <span className="inline-flex items-center gap-1 text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
                              <CheckCircle className="h-4 w-4" />
                              Prioritized
                            </span>
                          ) : (
                            <span className="text-sm text-[var(--text-ter)]">Below cut line</span>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
      )}

      {/* ── The prioritization memo (replaced the Strategic Narrative panel, DEP-032) ── */}
      {!matrixVariant && effectiveRankedRows.length > 0 && (
        <div className="mb-6">
          <SessionMemoAction sessionId={session.id} variant="panel" />
        </div>
      )}

      {/* ── Section 4: Participants ────────────────────────────────────────── */}
      <section className="bg-[var(--bg-raised)] border border-[var(--rule)] mb-6">
        <div className="px-6 py-4 border-b border-[var(--rule)] bg-[var(--surface)]">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-[var(--text-sec)]" />
            <h2 className="text-lg font-semibold text-[var(--text-pri)]">Participants</h2>
          </div>
        </div>
        <div className="p-6">
          {session.participantIds.length > 0 ? (
            <ul className="space-y-2">
              {session.participantIds.map(participantId => (
                <li key={participantId} className="flex items-center gap-3 text-sm">
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <User className="h-4 w-4 text-primary-600" />
                  </div>
                  <span className="text-[var(--text-pri)]">
                    {hideRecordNames && participantId !== session.facilitatorId
                      ? 'A participant'
                      : actorDisplayMap[participantId] ?? participantId}
                  </span>
                  {participantId === session.facilitatorId && (
                    <span className="px-2 py-0.5 bg-[var(--mustard-dim)] text-[var(--mustard)] text-xs rounded">
                      Facilitator
                    </span>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-[var(--text-ter)]">No participants were assigned.</p>
          )}
        </div>
      </section>

      {/* ── Section 5: Overrides ──────────────────────────────────────────── */}
      <section className="bg-[var(--bg-raised)] border border-[var(--rule)]">
        <div className="px-6 py-4 border-b border-[var(--rule)] bg-[var(--surface)]">
          <div className="flex items-center gap-2">
            <ArrowUpDown className="h-5 w-5 text-[var(--text-sec)]" />
            <h2 className="text-lg font-semibold text-[var(--text-pri)]">
              Overrides ({overrides.length})
            </h2>
          </div>
        </div>
        <div className="p-6">
          {overrides.length > 0 ? (
            <RankOverrideList overrides={overrides} getItemTitle={getItemTitle} />
          ) : (
            <div className="text-center py-4">
              <CheckCircle className="h-8 w-8 text-[var(--green)] mx-auto mb-2" />
              <p className="text-sm text-[var(--text-ter)]">No manual overrides were applied.</p>
            </div>
          )}
        </div>
      </section>

      <div className="mt-6 text-center text-xs text-[var(--text-ter)]">
        <p>Immutable record — Session ID: {session.id}</p>
        {dbRows.length > 0 && (
          <p className="mt-1">Results source: <code>session_results</code> table ({dbRows.length} rows)</p>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Matrix / classification tool-native result view
// ============================================================================

interface MatrixSessionResultsViewProps {
  variant: MatrixReportVariant;
  rows: Array<{ itemId: string; rank: number; score: number | null; classification: string | null; toolId: string | null }>;
  rankingOutput: PrioritizationSession['rankingOutput'];
  getItemTitle: (id: string) => string;
}

function MatrixSessionResultsView({
  variant,
  rows,
  rankingOutput,
  getItemTitle,
}: MatrixSessionResultsViewProps) {
  const rankedIds = rows.map(r => r.itemId);
  const itemsMap = useMemo(
    () => new Map(rankedIds.map(id => [id, { title: getItemTitle(id) }])),
    [rankedIds, getItemTitle],
  );

  const gridOrder =
    variant === 'impact_effort'
      ? ['quick-wins', 'big-bets', 'fill-ins', 'avoid']
      : variant === 'eisenhower'
        ? ['ui', 'in', 'un', 'nn']
        : ['must', 'should', 'could', 'wont'];

  const byBucket: Record<string, typeof rows> = { unclassified: [] };
  for (const key of gridOrder) byBucket[key] = [];
  for (const row of rows) {
    const bucket = row.classification ?? 'unclassified';
    if (bucket in byBucket) byBucket[bucket].push(row);
    else byBucket.unclassified.push(row);
  }

  return (
    <div className="p-6 space-y-6">
      <p className="text-xs text-[var(--text-ter)]">
        Items are grouped by majority-vote placement across participants.
        {variant === 'impact_effort' && ' Scores are not ranked — quadrant placement is the result.'}
      </p>

      <MatrixToolResultsCharts
        variant={variant}
        rankedIds={rankedIds}
        output={rankingOutput}
        itemsMap={itemsMap}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {gridOrder.map(key => {
          const meta = quadrantMetaByKey(variant, key);
          const bucketRows = byBucket[key] ?? [];
          if (!meta || bucketRows.length === 0) return null;
          return (
            <div key={key} className={` border p-4 ${meta.bgClass} ${meta.borderClass}`}>
              <div className={`font-semibold text-sm mb-0.5 ${meta.textClass}`}>{meta.label}</div>
              {meta.subtitle && <div className="text-xs text-[var(--text-ter)] mb-3">{meta.subtitle}</div>}
              <ul className="space-y-1.5">
                {bucketRows.map(row => {
                  const agreement = rankingOutput?.classifications?.[row.itemId];
                  const pct = agreement && typeof agreement !== 'string'
                    ? (agreement as { agreement?: number }).agreement
                    : null;
                  return (
                    <li key={row.itemId} className="flex items-center justify-between gap-3 text-sm">
                      <span className="font-medium text-[var(--text-pri)]">{getItemTitle(row.itemId)}</span>
                      {pct != null && (
                        <span className="text-xs text-[var(--text-ter)] whitespace-nowrap">{pct}% agree</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
        {byBucket.unclassified.length > 0 && (
          <div className="border border-[var(--rule)] bg-[var(--surface)] p-4">
            <div className="font-semibold text-sm text-[var(--text-ter)] mb-3">Unclassified</div>
            <ul className="space-y-1.5">
              {byBucket.unclassified.map(row => (
                <li key={row.itemId} className="text-sm text-[var(--text-sec)]">{getItemTitle(row.itemId)}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

export default SessionResultsReport;
